import { Phone } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Gmaps from '@/assets/smartcity/gmps-icon.svg';
import BubbleEvent from '@/domains/traffic-monitoring/components/BubbleEvent/BubbleEvent';
import ButtonWhatsapp from '@/domains/traffic-monitoring/components/ButtonWhatsapp/ButtonWhatsapp';
import ModalSupport from '@/domains/traffic-monitoring/components/ModalSupport/ModalSupport';
import { supportContacts } from '@/domains/traffic-monitoring/constants/supportContacts.constant';
import type { ReportManualCreateBody } from '@/domains/traffic-monitoring/types/psc-dashboard.types';
import Button from '@/shared/components/ui/Button';
import DetailForm from '@/shared/components/ui/DetailForm/DetailForm';
import { toWhatsAppNumber } from '@/shared/utils/phone';
import { showToast } from '@/shared/utils/showToast';

interface ModalRegistrationManualProps {
  isOpen?: boolean;
  onClose?: () => void;
  onRegister?: (data: ReportManualCreateBody) => void;
}

const ModalRegistrationManual = ({ isOpen, onClose, onRegister }: ModalRegistrationManualProps) => {
  const { t } = useTranslation();
  const [reporterName, setReporterName] = useState('');
  const [numberOfVictims, setNumberOfVictims] = useState('');
  const [reporterPhoneNumber, setReporterPhoneNumber] = useState('');
  const [incidentLocation, setIncidentLocation] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [isSupportOpen, setIsSupportOpen] = useState(false);

  if (!isOpen) {
    return null;
  }

  const handleRegisterClick = () => {
    if (!reporterName.trim() || !incidentLocation.trim()) {
      showToast({
        type: 'error',
        title: 'Missing Fields',
        message: 'Report By and Report Location are required',
      });
      return;
    }
    onRegister?.({
      reporter_name: reporterName,
      incident_location: incidentLocation,
      number_of_victims: numberOfVictims ? Number(numberOfVictims) : 1,
      reporter_phone_number: reporterPhoneNumber || undefined,
      latitude: latitude ? Number(latitude) : undefined,
      longitude: longitude ? Number(longitude) : undefined,
    });
  };

  const handlePasteCoordinates = async () => {
    let text: string;
    try {
      text = await navigator.clipboard.readText();
    } catch {
      showToast({ type: 'error', title: 'Paste Failed', message: 'Could not read clipboard' });
      return;
    }

    const parts = text.split(',').map((part) => part.trim());
    const lat = parts[0] ? Number(parts[0]) : Number.NaN;
    const lng = parts[1] ? Number(parts[1]) : Number.NaN;
    const isValid =
      parts.length === 2 &&
      Number.isFinite(lat) &&
      Number.isFinite(lng) &&
      lat >= -90 &&
      lat <= 90 &&
      lng >= -180 &&
      lng <= 180;

    if (!isValid) {
      showToast({
        type: 'error',
        title: 'Invalid Coordinates',
        message: 'Clipboard must contain "latitude, longitude"',
      });
      return;
    }

    setLatitude(String(lat));
    setLongitude(String(lng));
  };

  return (
    <div className="fixed inset-0 flex p-10 items-center justify-center bg-black/50 z-[1000]">
      <div className="w-[40%] h-[calc(90%-40px)]  overflow-hidden p-6 flex flex-col gap-6  bg-db-card border bg-db-border rounded-[5px]">
        <div className="w-full h-full overflow-y-auto flex flex-col gap-6">
          <div className="flex flex-row gap-1 items-center justify-between">
            <div className="flex flex-col gap-1">
              <span className="text-text-grey capitalize dark:text-white title2medium">
                {t('cmn>master>report id')}
              </span>
              <span className="title1bold">{t('psc>report>not yet assigned')}</span>
            </div>
            <BubbleEvent eventTypes={'incoming'} />
          </div>

          <div className="grid grid-cols-2 w-full gap-6 items-center">
            <DetailForm
              label={t('psc>report>report by')}
              direction="col"
              inputType="text"
              value={reporterName}
              onChange={(e) => setReporterName(e.target.value)}
            />
            <DetailForm
              label={t('psc>report>number of victims')}
              direction="col"
              inputType="select"
              value={numberOfVictims}
              onChange={(e) => setNumberOfVictims(e.target.value)}
              optionSelect={[
                { value: '1', label: '1' },
                { value: '2', label: '2' },
                { value: '3', label: '3' },
                { value: '4', label: '4' },
                { value: '5', label: '5' },
              ]}
            />
          </div>

          <DetailForm
            label={t('psc>report>reporter phone number')}
            direction="col"
            inputType="number"
            className="relative"
            value={reporterPhoneNumber}
            onChange={(e) => setReporterPhoneNumber(e.target.value)}
          >
            <ButtonWhatsapp
              styleButton="absolute top-[45%] right-[20px]"
              label={t('psc>report>call')}
              disabled={!reporterPhoneNumber.trim()}
              onclick={() => {
                const waNumber = toWhatsAppNumber(reporterPhoneNumber);
                if (waNumber)
                  window.open(
                    `https://wa.me/call/+${waNumber}`,
                    '_blank',
                    'noopener,noreferrer,width=480,height=720'
                  );
              }}
            />
          </DetailForm>

          <div className="w-full h-fit flex flex-col gap-3">
            <DetailForm
              required={true}
              label={t('psc>report>report location')}
              direction="col"
              inputType="textarea"
              className="relative "
              styleTextArea="h-[200px]"
              value={incidentLocation}
              onChange={(e) => setIncidentLocation(e.target.value)}
              labelChildren={
                <div className="inline-flex items-center gap-3 ml-auto">
                  <ButtonWhatsapp
                    styleButton="!border-[#007AFF] !bg-[#F3F6FE] dark:!bg-transparent/40 "
                    label={t('psc>report>open maps')}
                    img={Gmaps}
                    styleText="capitalize"
                    tooltip={true}
                    disabled={(!latitude.trim() || !longitude.trim()) && !incidentLocation.trim()}
                    onclick={() => {
                      if (latitude.trim() && longitude.trim()) {
                        window.open(
                          `https://www.google.com/maps?q=${latitude},${longitude}`,
                          '_blank'
                        );
                        return;
                      }
                      // No coordinates yet - fall back to a text search on the
                      // incident location instead of staying disabled.
                      if (incidentLocation.trim()) {
                        window.open(
                          `https://www.google.com/maps?q=${encodeURIComponent(incidentLocation)}`,
                          '_blank'
                        );
                      }
                    }}
                  />
                </div>
              }
            />
          </div>

          <div className="grid grid-cols-[repeat(auto-fit,_minmax(150px,_1fr))] gap-6 items-end ">
            <DetailForm
              label="LATITUDE"
              direction="col"
              inputType="text"
              pattern={/^-?\d*\.?\d*$/}
              value={latitude}
              onChange={(e) => setLatitude(e.target.value)}
            />
            <DetailForm
              label="LONGITUDE"
              direction="col"
              inputType="text"
              pattern={/^-?\d*\.?\d*$/}
              value={longitude}
              onChange={(e) => setLongitude(e.target.value)}
            />
            <div className="col-span-2 md:col-span-1 grid">
              <Button
                bg-text-danger-300
                className="flex-1  btn-fill-blue text-[20px] cursor-pointer w-full line-clamp-1 text-ellipsis overflow-hidden "
                label={t('psc>report>paste coordinates')}
                onClick={handlePasteCoordinates}
              />
            </div>
          </div>

          <DetailForm
            label={t('cmn > description')}
            direction="col"
            inputType="textarea"
            className="relative "
            styleTextArea="h-[200px]"
          />

          <Button
            bg-text-danger-300
            className="flex-1  btn-fill-blue-opacity text-[20px] cursor-pointer "
            onClick={() => setIsSupportOpen(true)}
          >
            <Phone />
            {t('psc>report>call support')}
          </Button>
        </div>

        <div className="w-full inline-flex gap-3 items-center ">
          <Button
            bg-text-danger-300
            className="flex-1 btn-outline-blue  text-[20px]  cursor-pointer"
            label={t('cmn > close')}
            onClick={onClose}
          />
          <Button
            bg-text-danger-300
            className="flex-1  btn-fill-blue text-[20px] cursor-pointer "
            label={t('psc>report>register')}
            onClick={handleRegisterClick}
          />
        </div>
      </div>

      <ModalSupport
        isOpen={isSupportOpen}
        onClose={() => setIsSupportOpen(false)}
        contacts={supportContacts}
        onCall={(phone) => {
          const waNumber = toWhatsAppNumber(phone);
          if (waNumber)
            window.open(
              `https://wa.me/call/+${waNumber}`,
              '_blank',
              'noopener,noreferrer,width=480,height=720'
            );
        }}
      />
    </div>
  );
};

export default ModalRegistrationManual;
